import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Globe2 } from 'lucide-react';
import './flights/flights.css';
import { flightCall, Cinematic, DecisionCard, IMG, countryImg, fmt } from './flights/FlightScenes';
import { HubStage, DestinationGrid, HubHint, FlightDossier } from './flights/FlightHub';
import { Hangar } from './flights/Hangar';
import { FlightSequence } from './flights/FlightSequence';
import { AbroadView, MissionBriefing, CollectIncome } from './flights/AbroadView';

const pct = (x) => `${Math.round((x || 0) * 100)}%`;

function decisionProps(kind, offer, dest, cash) {
  if (kind === 'encounter') return { img: countryImg(dest?.country), kicker: 'ENCOUNTER', title: offer.title, narrative: offer.narrative, choices: offer.choices };
  if (kind === 'intercept') {
    return {
      img: IMG.customs, kicker: `POLICE INTERCEPT · ${(offer.severity || '').toUpperCase()}`, title: 'YOU\'VE BEEN STOPPED', narrative: offer.narrative,
      stats: [['Bribe', fmt(offer.bribe_cost)], ['Seizure', `${pct(offer.cash_seized_pct)} cash · ${pct(offer.product_seized_pct)} product`], ['Escape odds', `${offer.escape_chance}%`]],
      choices: [
        { id: 'bribe', label: `Pay the bribe · ${fmt(offer.bribe_cost)}`, desc: cash < offer.bribe_cost ? 'Not enough cash on you.' : 'Make it disappear. Costs cash only.', disabled: cash < offer.bribe_cost },
        { id: 'seize', label: 'Accept the seizure', desc: `They take ${pct(offer.cash_seized_pct)} of your cash and ${pct(offer.product_seized_pct)} of the product. +${offer.heat_gain} heat.` },
        { id: 'run', label: 'Run for it', desc: `${offer.escape_chance}% to get away clean. If caught, they take double and the heat spikes.` },
      ],
    };
  }
  return {
    img: IMG.customs, kicker: 'U.S. CUSTOMS · JFK', title: 'SECONDARY INSPECTION', narrative: offer.narrative,
    stats: [['Bribe', fmt(offer.bribe_cost)], ['Search risk', `${offer.customs_risk}%`]],
    choices: [
      { id: 'bribe', label: `Bribe the officer · ${fmt(offer.bribe_cost)}`, desc: cash < offer.bribe_cost ? 'Not enough cash on you.' : 'Walk through untouched.', disabled: cash < offer.bribe_cost },
      { id: 'declare', label: 'Declare it', desc: `Lose ${pct(offer.product_seized_pct)} of the product, keep your cash. Half the usual heat.` },
      { id: 'smuggle', label: 'Smuggle it through', desc: `${offer.customs_risk}% chance they find it and confiscate a chunk.` },
    ],
  };
}

const DECISION_PATH = { encounter: ['flights/encounter', 'choice_id'], intercept: ['flights/intercept', 'choice'], customs: ['flights/customs', 'choice'] };

export default function FlightsTab({ gameId, onRefresh }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [hangar, setHangar] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [note, setNote] = useState(null);
  const [cine, setCineRaw] = useState(null);
  const cineId = useRef(0);
  const setCine = (c) => setCineRaw(c && { ...c, id: ++cineId.current });
  const [flight, setFlight] = useState(null);
  const [mission, setMission] = useState(null);
  const [decision, setDecision] = useState(null);

  const load = useCallback(async (hydrate = false) => {
    const d = await flightCall(gameId, 'flights');
    setData(d);
    const f = d.active_flight;
    if (!hydrate) return d;
    if (f?.pending_intercept) setDecision((x) => x || { kind: 'intercept', offer: f.pending_intercept });
    else if (f?.pending_encounter) setDecision((x) => x || { kind: 'encounter', offer: f.pending_encounter });
    else if (f?.pending_customs) setDecision((x) => x || { kind: 'customs', offer: f.pending_customs });
    return d;
  }, [gameId]);

  useEffect(() => { load(true).catch((e) => setErr(e.message)); }, [load]);

  const overlayOpen = !!(cine || mission || decision || flight || hangar);
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('kow-flight-overlay', { detail: overlayOpen }));
  }, [overlayOpen]);
  useEffect(() => () => window.dispatchEvent(new CustomEvent('kow-flight-overlay', { detail: false })), []);

  const lock = useRef(false);
  const guard = async (fn) => {
    if (lock.current) return;
    lock.current = true; setBusy(true); setErr(null);
    try { await fn(); } catch (e) { setErr(e.message); } finally { lock.current = false; setBusy(false); }
  };

  const arriveHome = (res) => setCine({
    steps: [{ img: IMG.brooklyn, kicker: 'WELCOME BACK', title: 'BROOKLYN', sub: res.narrator, dur: 3800 }],
    onDone: () => { setCine(null); setNote(res.narrator); },
  });

  const book = (d) => guard(async () => {
    await flightCall(gameId, 'flights/book', { destination: d.id });
    setSelected(null);
    await load(); onRefresh?.();
    setFlight({ from: 'brooklyn', to: d.country, city: d.name.split(',')[0], lat: d.lat, lng: d.lng, ...jetInfo(), onDone: () => setFlight(null) });
  });

  const runMission = () => guard(async () => {
    const res = await flightCall(gameId, 'flights/mission', { destination: data.active_flight.destination, mission_id: mission.m.id });
    setMission((x) => ({ ...x, outcome: res }));
    await load();
  });

  const closeMission = () => {
    const res = mission?.outcome;
    setMission(null);
    setErr(null);
    if (res && !res.police_intercept && !res.encounter) onRefresh?.();
    if (res?.police_intercept) setDecision({ kind: 'intercept', offer: res.police_intercept });
    else if (res?.encounter) setDecision({ kind: 'encounter', offer: res.encounter });
  };

  const choose = (choice) => guard(async () => {
    const [path, key] = DECISION_PATH[decision.kind];
    const res = await flightCall(gameId, path, { [key]: choice }).catch(async (e) => { await load().catch(() => {}); throw e; });
    setDecision((x) => ({ ...x, result: res }));
    await load();
  });

  const continueDecision = () => {
    const { kind, result } = decision;
    setDecision(null);
    setErr(null);
    onRefresh?.();
    if (kind === 'customs') arriveHome(result);
  };

  const flyHome = () => guard(async () => {
    const res = await flightCall(gameId, 'flights/return', {});
    const leg = { from: dest?.country || 'colombia', to: 'brooklyn', city: (dest?.name || 'ABROAD').split(',')[0], lat: dest?.lat, lng: dest?.lng, ...jetInfo() };
    await load();
    if (res.pending_customs) {
      setFlight({ ...leg, onDone: () => { setFlight(null); setDecision({ kind: 'customs', offer: res.pending_customs }); } });
      return;
    }
    onRefresh?.();
    setFlight({ ...leg, onDone: () => { setFlight(null); arriveHome(res); } });
  });

  const jetInfo = () => {
    const j = (data?.jets || []).find((x) => x.active);
    return { jet: j?.id || 'citation', jetName: j?.name || 'Charter Citation' };
  };
  const jetCall = (path, jetId) => guard(async () => {
    await flightCall(gameId, path, { jet_id: jetId });
    await load(); onRefresh?.();
  });

  const act = (path, body) => guard(async () => {
    const res = await flightCall(gameId, path, body);
    setNote(res.message || res.narrator || (res.total_income !== undefined ? `Collected ${fmt(res.total_income)} in safehouse income.` : 'Done.'));
    await load(); onRefresh?.();
  });

  if (!data) return <div data-testid="flights-loading" className="h-60 flex items-center justify-center text-white/50">{err || <Loader2 className="animate-spin" />}</div>;
  const active = data.active_flight;
  const dest = active && data.destinations.find((d) => d.id === active.destination);
  const selectedDest = selected && data.destinations.find((d) => d.id === selected.id);
  const showDecision = decision && !cine && !flight;

  return (
    <div data-testid="flights-tab" className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] tracking-[0.35em] text-[#C9A84C] flex items-center gap-1.5"><Globe2 size={12} /> INTERNATIONAL FLIGHTS</p>
          <h2 className="font-['Bebas_Neue',sans-serif] text-4xl sm:text-5xl text-white leading-none">{active ? 'ON THE GROUND' : 'THE CONNECT'}</h2>
        </div>
        <div className="flex items-center gap-2 text-xs text-white/60"><span data-testid="flights-cash">Cash {fmt(data.cash)}</span>{!active && <CollectIncome busy={busy} act={act} owns={data.owns_safe_house} nextAt={data.next_collect_at} />}</div>
      </div>
      {err && <p data-testid="flights-error" className="rounded-lg border border-[#F87171]/40 bg-[#7f1d1d]/30 px-3 py-2 text-sm text-[#FECACA]">{err}</p>}
      {note && <button type="button" data-testid="flights-note" onClick={() => setNote(null)} className="w-full text-left rounded-lg border border-[#C9A84C]/40 bg-[#C9A84C]/10 px-3 py-2 text-sm text-[#F3DC8A]">{note}</button>}
      {active && dest ? (
        <AbroadView dest={dest} flight={active} busy={busy} onMission={(m) => setMission({ m })} onReturn={flyHome} act={act} />
      ) : (
        <>
          <div>
            <HubStage level={data.level} paused={!!(cine || flight || hangar)} livery={data.active_jet} onHangar={() => { setErr(null); setHangar(true); }} />
            <DestinationGrid data={data} selected={selected} onSelect={(d) => { setErr(null); setSelected(d); }} />
          </div>
          <HubHint allLocked={!data.destinations.length && data.locked_destinations.length > 0} minEra={Math.min(...data.locked_destinations.map((d) => d.min_era))} />
          {hangar && <Hangar jets={data.jets} busy={busy} error={err} onBuy={(id) => jetCall('flights/jet/buy', id)} onSelect={(id) => jetCall('flights/jet/select', id)} onClose={() => setHangar(false)} />}
          {selectedDest && !flight && <FlightDossier dest={selectedDest} data={data} busy={busy} error={err} onBook={book} onClose={() => setSelected(null)} />}
        </>
      )}
      {mission && dest && <MissionBriefing mission={mission.m} dest={dest} busy={busy} error={err} outcome={mission.outcome} onRun={runMission} onClose={closeMission} />}
      {showDecision && <DecisionCard testId={`flight-${decision.kind}`} {...decisionProps(decision.kind, decision.offer, dest, data.cash)} error={err} busy={busy} result={decision.result} onChoose={choose} onContinue={continueDecision} />}
      {cine && <Cinematic key={cine.id} steps={cine.steps} onDone={cine.onDone} />}
      {flight && <FlightSequence key={`${flight.from}-${flight.to}`} {...flight} />}
    </div>
  );
}
